import React, { useEffect, useState } from 'react';
import { Heart, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import { getPlayerProfile } from '../../../../services/firestoreService';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * DonationsEditor - Editor component for the donations block
 * Shows Stripe connection status and customization options
 */
const DonationsEditor = ({ block, onChange, profileId, theme, hideStylePanels }) => {
  const { content } = block;
  const [stripeConnected, setStripeConnected] = useState(false);
  const [donationsEnabled, setDonationsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDonationInfo = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      try {
        // Get profile to check Stripe status
        const profile = await getPlayerProfile(profileId);
        setStripeConnected(!!profile?.stripeAccountId);
        setDonationsEnabled(!!profile?.donationsEnabled);
      } catch (error) {
        console.error('Error loading donation info:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDonationInfo();
  }, [profileId]);

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <div className="animate-spin w-5 h-5 border-2 border-gray-600 border-t-[#749555] rounded-full mr-2"></div>
        Loading...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status Card */}
      <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#749555]/20 rounded-lg">
            <Heart size={24} className="text-[#749555]" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-medium mb-1">Donations</h4>
            {stripeConnected && donationsEnabled ? (
              <div className="flex items-center gap-2 text-sm text-green-400">
                <CheckCircle size={14} />
                <span>Stripe connected & donations enabled</span>
              </div>
            ) : stripeConnected ? (
              <div className="flex items-center gap-2 text-sm text-amber-400">
                <AlertCircle size={14} />
                <span>Stripe connected but donations not enabled</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-amber-400">
                <AlertCircle size={14} />
                <span>Connect Stripe to accept donations</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Section Heading
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="Support My Work"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Description (optional)
        </label>
        <textarea
          value={content.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Your support helps me create more content and attend tournaments!"
          rows={3}
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none resize-none"
        />
      </div>

      {/* Button Text */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Button Text
        </label>
        <input
          type="text"
          value={content.buttonText || ''}
          onChange={(e) => handleChange('buttonText', e.target.value)}
          placeholder="Donate"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (<>
        <SizeSpacingPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={BLOCK_FEATURES['donations']}
        />
        <ColorsFontsPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={BLOCK_FEATURES['donations']}
          theme={theme || {}}
          blockType="donations"
        />
      </>)}

      {/* Manage Donations Link */}
      <a
        href="/dashboard/website/donations"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#749555] hover:bg-[#8bab6a] text-white rounded-lg transition-colors text-sm font-medium"
      >
        {stripeConnected ? 'Manage Donations' : 'Connect Stripe'}
        <ExternalLink size={14} />
      </a>
    </div>
  );
};

export default DonationsEditor;
